import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import ChatContainer from './components/ChatContainer';
import InputArea from './components/InputArea';
import CanineHealthCheck from './components/CanineHealthCheck';
import FeedingCalculator from './components/FeedingCalculator';
import { Message, ResearchPaper } from './types';
import { apiService, ChatRequest, WebSearchResult } from './services/api';

const App: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [interactionCount, setInteractionCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchStatus, setSearchStatus] = useState<string>('');
  const [currentWebSearchResults, setCurrentWebSearchResults] = useState<WebSearchResult[]>([]);
  const [showHealthCheck, setShowHealthCheck] = useState(false);
  const [showFeedingCalculator, setShowFeedingCalculator] = useState(false);
  const [appMode, setAppMode] = useState<'pawco' | 'genius-dog'>(() => {
    const hostname = window.location.hostname;
    
    // Check if we're on the Genius Dog chatbot subdomain
    if (hostname.includes('chat.geniusdog')) {
      sessionStorage.setItem('genius-dog-authenticated', 'true');
      return 'genius-dog';
    }
    
    // Default to PawCo mode (for chat.mypawco.com)
    return 'pawco';
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // On initial load, scroll chat to top so nothing is hidden behind the fixed header
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    // Only auto-scroll to bottom when there are messages
    if (messages.length > 0 || isTyping) {
      scrollToBottom();
    }
  }, [messages, isTyping]);

  const sendMessage = async (messageText: string, navContext?: string) => {
    if (!messageText.trim()) return;

    // Clear any previous errors
    setError(null);

    // Hide welcome section only after first interaction
    if (showWelcome && messages.length === 0) {
      setShowWelcome(false);
    }

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: messageText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInteractionCount(prev => prev + 1);
    setIsTyping(true);

    // Create bot message ID but don't add the message until we start receiving content
    const botMessageId = Date.now() + 1;

      try {
        // Set searching state - web search is always enabled
        setIsSearching(true);
        setSearchStatus('Analyzing your question...');

      // Prepare API request with mode-specific context
      const request: ChatRequest = {
        message: appMode === 'genius-dog' ? `[GENIUS DOG MARKETING AI] ${messageText}` : messageText,
        conversationId,
        useWebSearch: true, // Always enabled
        context: apiService.createPetContext(navContext || messageText)
      };

      // Send streaming request
      const newConversationId = await apiService.sendStreamingMessage(
        request,
        // On chunk received
        (content: string) => {
          
          // Stop loading animations on first content chunk
          setIsSearching(false);
          setIsTyping(true); // Keep typing indicator while streaming
          setSearchStatus('');
          
          setMessages(prev => {
            const existingBotMessage = prev.find(msg => msg.id === botMessageId.toString());
            
            if (existingBotMessage) {
              // Update existing message
              return prev.map(msg => 
                msg.id === botMessageId.toString() 
                  ? { ...msg, text: msg.text + content }
                  : msg
              );
            } else {
              // Create new bot message on first content
              const newBotMessage: Message = {
                id: botMessageId.toString(),
                text: content,
                sender: 'bot',
                timestamp: new Date()
              };
              return [...prev, newBotMessage];
            }
          });
        },
        // On complete
        (messageId: string, metadata?: any, webSearchResults?: WebSearchResult[], researchPapers?: ResearchPaper[], classification?: any) => {
          console.log('Stream completed with:', { messageId, webSearchResults, researchPapers, metadata });
          
          setIsTyping(false);
          setIsSearching(false);
          setSearchStatus('');
          
          // Update message with both web search results and research papers in a single update
          setMessages(prev => prev.map(msg =>
            msg.id === botMessageId.toString()
              ? { 
                  ...msg, 
                  webSearchResults: webSearchResults || msg.webSearchResults,
                  researchPapers: researchPapers || msg.researchPapers
                }
              : msg
          ));
          
          // Also update the current web search results state
          if (webSearchResults && webSearchResults.length > 0) {
            setCurrentWebSearchResults(webSearchResults);
          }
        },
        // On error
        (errorMsg: string) => {
          setIsTyping(false);
          setIsSearching(false);
          setSearchStatus('');
          setError(errorMsg);
          
          // Update the bot message with error
          setMessages(prev => prev.map(msg => 
            msg.id === botMessageId.toString() 
              ? { 
                  ...msg, 
                  text: `❌ Sorry, I encountered an error: ${errorMsg}\n\n💡 Make sure the backend server is running at http://localhost:5000`
                }
              : msg
          ));
        },
        // On search status
        (status: string) => {
          setSearchStatus(status);
        }
      );

      // Update conversation ID if this is a new conversation
      if (!conversationId && newConversationId) {
        setConversationId(newConversationId);
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      setIsTyping(false);
      setIsSearching(false);
      setSearchStatus('');
      
      // Update the bot message with error
      setMessages(prev => prev.map(msg => 
        msg.id === botMessageId.toString() 
          ? { 
              ...msg, 
              text: `❌ Sorry, I'm having trouble connecting to my brain right now. ${error instanceof Error ? error.message : 'Please try again in a moment.'}\n\n💡 Make sure the backend server is running at http://localhost:5000`
            }
          : msg
      ));
      
      setError(error instanceof Error ? error.message : 'Connection error');
    }
  };

  const generateBotResponse = (userMessage: string): string => {
    const responses = {
      puppy: [
        "🐶 Great question about puppy nutrition! Puppies need high-quality protein for healthy growth. Look for foods with real meat as the first ingredient and appropriate puppy-specific formulations. Would you like specific brand recommendations?",
        "🐾 Puppy food should have higher protein and fat content than adult food. Pawco's formulas are scientifically developed for growth and reproduction. How old is your puppy?"
      ],
      senior: [
        "🐕‍🦺 Senior dogs benefit from easily digestible proteins and joint-supporting ingredients like glucosamine. Lower calorie formulas help prevent weight gain. What's your dog's current age and any health concerns?",
        "🧓 Senior pet nutrition should focus on maintaining muscle mass while being easier to digest. Look for foods with omega-3 fatty acids for cognitive health."
      ],
      ingredients: [
        "⚠️ Avoid chocolate, grapes, onions, garlic, xylitol, and excessive salt. Look for whole proteins, avoid by-products and fillers. Quality ingredients make a huge difference in your pet's health!",
        "📋 Always check for artificial preservatives like BHA, BHT, and ethoxyquin. Natural preservatives like vitamin E and C are much better options."
      ],
      default: [
        "🐾 That's a great question! At Pawco, we believe every dog deserves the best nutrition. Could you tell me more about your dog's specific needs - age, breed, size, and any health concerns?",
        "🐕 I'd be happy to help you find the perfect food for your canine companion! What type of dog do you have, and are there any specific dietary requirements or preferences?",
        "💡 Every dog is unique! To give you the best recommendation, I'd love to know more about your dog's age, activity level, and any food sensitivities they might have."
      ]
    };

    const lowerMessage = userMessage.toLowerCase();

    let responseCategory = 'default';
    if (lowerMessage.includes('puppy') || lowerMessage.includes('young dog')) {
      responseCategory = 'puppy';
    } else if (lowerMessage.includes('senior') || lowerMessage.includes('old') || lowerMessage.includes('elderly')) {
      responseCategory = 'senior';
    } else if (lowerMessage.includes('ingredient') || lowerMessage.includes('avoid') || lowerMessage.includes('harmful')) {
      responseCategory = 'ingredients';
    }

    const categoryResponses = responses[responseCategory as keyof typeof responses];
    return categoryResponses[Math.floor(Math.random() * categoryResponses.length)];
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  const handleNavigation = (navItem: string) => {
    switch(navItem) {
      case 'Canine Health Check':
        setShowHealthCheck(true);
        break;
      case 'Feeding Calculator':
        setShowFeedingCalculator(true);
        break;
    }
  };

  return (
    <div className={`app-layout light ${appMode === 'genius-dog' ? 'genius-dog-mode' : 'pawco-mode'}`}>
        {/* Mobile backdrop overlay */}
        {!sidebarCollapsed && (
          <div className="mobile-backdrop" onClick={toggleSidebar}></div>
        )}
        
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={toggleSidebar}
          onNavigation={handleNavigation}
          interactionCount={interactionCount}
          appMode={appMode}
        />
      <main className="main-container">
        <header className="main-header">
          <div className="header-content">
            <button className="mobile-menu-toggle" onClick={toggleSidebar}>
              <i className="fas fa-bars"></i>
            </button>
            <div className="header-title">
              <h1>{appMode === 'genius-dog' ? 'Genius Dog Premium Line' : "Your Dog's Wellness Assistant"}</h1>
              <p>{appMode === 'genius-dog' ? 'Advanced Dog Food Products & Nutrition Science' : 'Built on Veterinary Research. Designed for Your Dog.'}</p>
            </div>
            <div className="header-status">
              <div className="status-indicator">
                <span className={`status-dot ${error ? 'error' : ''}`}></span>
                <span className="status-text">
                  {error ? 'Connection Error' : 'Ready'}
                </span>
              </div>
              {error && (
                <div className="error-message">
                  <span>⚠️ {error}</span>
                </div>
              )}
            </div>
          </div>
        </header>

            <ChatContainer
              messages={messages}
              showWelcome={showWelcome}
              isTyping={isTyping}
              isSearching={isSearching}
              searchStatus={searchStatus}
              messagesEndRef={messagesEndRef}
              chatMessagesRef={chatMessagesRef}
              appMode={appMode}
              onSuggestionClick={sendMessage}
            />

                <InputArea
                  onSendMessage={sendMessage}
                  isSearching={isSearching}
                  searchStatus={searchStatus}
                />
      </main>

      {/* Modals */}
      {showHealthCheck && (
        <CanineHealthCheck onClose={() => setShowHealthCheck(false)} />
      )}
      
      {showFeedingCalculator && (
        <FeedingCalculator onClose={() => setShowFeedingCalculator(false)} />
      )}
    </div>
  );
};

export default App;
